/**
 * Crew ▸ Sign On / Off (Joining / Sign-Off; ADR 0131 §3, §4, Ek 2): `gemi-personel.jpeg` panel 5.
 * Hareketi ofis kaydeder; gemide liste salt okunurdur.
 */

export const signOnOffEn = {
  title: 'Sign On / Off',
  subtitle: 'Joining and sign-off records of the fleet.',
  subtitleShip: 'Joining and sign-off records of {{vessel}}. Recorded by the office.',
  error: 'The joining and sign-off records could not be loaded.',
  tabs: { label: 'Sign On / Off sections', joining: 'Joining', sign_off: 'Sign-Off' },
  filters: 'Filters',
  allVessels: 'All Vessels',
  allRanks: 'All Ranks',
  vessel: 'Vessel',
  rank: 'Rank',
  dateRange: 'Date range',
  from: 'From',
  to: 'To',
  addJoining: 'Add Joining',
  addSignOff: 'Add Sign-Off',
  table: 'Joining and sign-off records',
  columns: {
    name: 'Name',
    rank: 'Rank',
    vessel: 'Vessel',
    joiningDate: 'Joining Date',
    signOffDate: 'Sign-Off Date',
    port: 'Port',
    reason: 'Reason',
    status: 'Status',
    actions: 'Actions',
  },
  status: { pending: 'Pending', completed: 'Completed' },
  rankChangeBadge: 'Rank change',
  empty: 'No joining or sign-off in this period.',
  emptyShip: 'No joining or sign-off of this vessel in this period.',
  noMatch: 'No record matches the filters.',
  showing: 'Showing {{from}} - {{to}} of {{total}}',
  pagination: 'Pages',
  page: {
    first: 'First page',
    last: 'Last page',
    next: 'Next page',
    previous: 'Previous page',
  },
  readOnlyShip: 'Joining and sign-off are recorded by the office. On board the list is read-only.',
  exportExcel: 'Export Excel',
  exportFailed: 'The Excel file could not be produced. Try again.',
  export: {
    title: 'Sign On / Off — {{tab}}',
    period: 'Period {{from}} – {{to}}',
    generated: 'Generated {{at}} UTC',
    crewCode: 'Crew ID',
    portCode: 'UN/LOCODE',
    contractNo: 'Contract No',
    contractEnd: 'Contract end',
    joiningDate: 'Joining Date',
    rankChange: '{{rank}} (Rank change)',
  },
  actionsFor: 'Actions for {{name}}',
  action: {
    openProfile: 'Crew Profile',
    signOff: 'Sign off',
    rankChange: 'Rank change',
    rankChangeNotJoined: 'Not joined yet. Use Amend to change the rank.',
    amend: 'Amend',
    cancel: 'Cancel joining',
    revoke: 'Revoke sign-off',
    history: 'History',
  },
  reason: {
    end_of_contract: 'End of contract',
    medical: 'Medical',
    compassionate: 'Compassionate',
    crew_request: 'Crew request',
    company_request: 'Company request',
    disciplinary: 'Disciplinary',
    transfer: 'Transfer',
    promotion: 'Promotion',
    vessel_sale_or_layup: 'Vessel sale or lay-up',
    other: 'Other',
  },
  kind: {
    sign_on: 'Sign on',
    sign_off: 'Sign off',
    rank_change: 'Rank change',
    amend: 'Amended',
    cancel_joining: 'Joining cancelled',
    revoke_sign_off: 'Sign-off revoked',
  },
  contractStatus: {
    planned: 'Planned',
    active: 'Active',
    overdue: 'Overdue',
    completed: 'Completed',
  },
  dialog: {
    signOnTitle: 'Add Joining',
    signOnFor: 'Sign on {{name}}',
    signOffTitle: 'Sign off {{name}}',
    rankChangeTitle: 'Rank change for {{name}}',
    amendTitle: 'Amend record of {{name}}',
    cancelTitle: 'Cancel joining of {{name}}',
    revokeTitle: 'Revoke sign-off of {{name}}',
    historyTitle: 'History of {{name}}',
    person: 'Crew member',
    personPlaceholder: 'Search crew card',
    personHelp: 'Only crew cards are listed. Nationality and date of birth are required.',
    vessel: 'Vessel',
    rank: 'Rank',
    joiningDate: 'Joining date',
    signOffDate: 'Sign-off date',
    newRankFrom: 'New rank from',
    newRankHelp: 'The current record ends the day before; continuous service is kept.',
    joinedAs: 'Joined {{date}} as {{rank}}',
    joinsAs:
      'Joins {{date}} as {{rank}} (not joined yet). To correct the rank before joining, use Amend.',
    port: 'Port',
    portCode: 'UN/LOCODE',
    portCodePlaceholder: 'e.g. TRMER',
    contractEnd: 'Contract end',
    contractNo: 'Contract No',
    // Kayıtlı aylık maaş (ADR 0165 §1): yalnız maaş izni olana
    monthlyWage: 'Monthly wage',
    wageCurrency: 'Wage currency',
    wageHelp:
      'Optional. Used when the office opens a payroll period; visible only with payroll permission.',
    contractReason: 'Reason (contract beyond 12 months)',
    contractReasonHelp:
      'The last day on board under MLC is {{date}}. A longer contract needs a reason.',
    reason: 'Reason',
    remarks: 'Remarks',
    remarksRequired: 'Remarks are required for "Other".',
    correctionReason: 'Reason for the change',
    correctionHelp: 'Required when the joining or the sign-off has already taken place.',
    cancelHelp:
      'Only a joining that has not taken place can be cancelled. The record is kept in the history.',
    revokeHelp: 'The record becomes open again.',
    cancel: 'Cancel',
    save: 'Save',
    signOn: 'Sign On',
    signOff: 'Sign Off',
    confirmCancel: 'Cancel joining',
    confirmRevoke: 'Revoke sign-off',
    warnings: 'Signed on. Check before joining:',
    close: 'Close',
    historyEmpty: 'No history.',
    by: 'by {{name}}',
  },
  warnings: {
    PASSPORT_MISSING: 'No valid passport on the crew card.',
    SEAMAN_BOOK_MISSING: "No valid Seaman's Book on the crew card.",
  },
  errors: {
    VALIDATION_FAILED: 'Check the marked fields.',
    INVALID_LOCAL_DATE: 'Enter a valid date.',
    INVALID_DATE_RANGE: 'The dates are not in order.',
    CREW_PROFILE_INCOMPLETE:
      'Nationality and date of birth must be on the crew card before signing on.',
    UNDER_MINIMUM_AGE: 'The crew member is under 16 on the joining date (MLC A1.1).',
    CREW_ASSIGNMENT_OVERLAP: 'The crew member is on another record on these dates.',
    DATE_TOO_FAR: 'The date can be at most one year ahead.',
    SIGN_OFF_BEFORE_JOINING: 'The sign-off date cannot be before the joining date.',
    RANK_CHANGE_BEFORE_JOINING:
      'The new rank must start after the joining date ({{date}}). To correct the rank before joining, use Amend.',
    RANK_CHANGE_AFTER_SIGN_OFF:
      'The new rank must start on or before the sign-off date ({{date}}). To change the sign-off, use Amend.',
    REASON_REQUIRED: 'Enter a reason.',
    ALREADY_SIGNED_OFF: 'This record is already signed off. Use Amend to change it.',
    NOT_SIGNED_OFF: 'This record has no sign-off.',
    JOINING_COMPLETED:
      'The joining has already taken place and cannot be cancelled. Use Sign off or Amend.',
    CONTRACT_EXCEEDS_MLC: 'The contract is beyond the MLC 12-month limit. Enter a reason.',
    USE_SIGN_ON_OFF: 'This person has a crew card. Change the record in Sign On / Off.',
    NO_CHANGE: 'Nothing was changed.',
    UNKNOWN_VESSEL: 'Choose a vessel.',
    UNKNOWN_ROLE: 'Choose a rank.',
    ROLE_KIND_MISMATCH: 'An office title cannot be a rank. Choose a vessel rank.',
    ROW_VERSION_CONFLICT: 'The record was changed by someone else. Reload and try again.',
    FORBIDDEN: 'You do not have permission for this.',
    NOT_FOUND: 'The record was not found.',
    GENERIC: 'The record could not be saved. Try again.',
  },
};

export const signOnOffTr: typeof signOnOffEn = {
  title: 'Katılış / Ayrılış',
  subtitle: 'Filonun katılış ve ayrılış kayıtları.',
  subtitleShip: '{{vessel}} katılış ve ayrılış kayıtları. Ofis kaydeder.',
  error: 'Katılış ve ayrılış kayıtları yüklenemedi.',
  tabs: { label: 'Katılış / Ayrılış bölümleri', joining: 'Katılış', sign_off: 'Ayrılış' },
  filters: 'Süzgeçler',
  allVessels: 'Tüm Gemiler',
  allRanks: 'Tüm Rütbeler',
  vessel: 'Gemi',
  rank: 'Rütbe',
  dateRange: 'Tarih aralığı',
  from: 'Başlangıç',
  to: 'Bitiş',
  addJoining: 'Katılış Ekle',
  addSignOff: 'Ayrılış Ekle',
  table: 'Katılış ve ayrılış kayıtları',
  columns: {
    name: 'Ad',
    rank: 'Rütbe',
    vessel: 'Gemi',
    joiningDate: 'Katılış Tarihi',
    signOffDate: 'Ayrılış Tarihi',
    port: 'Liman',
    reason: 'Neden',
    status: 'Durum',
    actions: 'İşlemler',
  },
  status: { pending: 'Bekliyor', completed: 'Tamamlandı' },
  rankChangeBadge: 'Rütbe değişikliği',
  empty: 'Bu dönemde katılış ya da ayrılış yok.',
  emptyShip: 'Bu dönemde bu geminin katılış ya da ayrılışı yok.',
  noMatch: 'Süzgeçlere uyan kayıt yok.',
  showing: '{{total}} kayıttan {{from}} - {{to}}',
  pagination: 'Sayfalar',
  page: {
    first: 'İlk sayfa',
    last: 'Son sayfa',
    next: 'Sonraki sayfa',
    previous: 'Önceki sayfa',
  },
  readOnlyShip: 'Katılış ve ayrılışı ofis kaydeder. Gemide liste salt okunurdur.',
  exportExcel: 'Excel’e aktar',
  exportFailed: 'Excel dosyası oluşturulamadı. Yeniden deneyin.',
  export: {
    title: 'Katılış / Ayrılış — {{tab}}',
    period: 'Dönem {{from}} – {{to}}',
    generated: 'Oluşturulma {{at}} UTC',
    crewCode: 'Personel no',
    portCode: 'UN/LOCODE',
    contractNo: 'Kontrat No',
    contractEnd: 'Kontrat bitişi',
    joiningDate: 'Katılış Tarihi',
    rankChange: '{{rank}} (Rütbe değişikliği)',
  },
  actionsFor: '{{name}} için işlemler',
  action: {
    openProfile: 'Personel Kartı',
    signOff: 'Ayrılış kaydet',
    rankChange: 'Rütbe değişikliği',
    rankChangeNotJoined: 'Henüz katılmadı. Rütbeyi değiştirmek için Düzelt’i kullanın.',
    amend: 'Düzelt',
    cancel: 'Katılışı iptal et',
    revoke: 'Ayrılışı geri al',
    history: 'Geçmiş',
  },
  reason: {
    end_of_contract: 'Kontrat bitişi',
    medical: 'Sağlık',
    compassionate: 'Ailevi (insani)',
    crew_request: 'Personel isteği',
    company_request: 'Şirket isteği',
    disciplinary: 'Disiplin',
    transfer: 'Başka gemiye geçiş',
    promotion: 'Terfi',
    vessel_sale_or_layup: 'Geminin satışı ya da bağlanması',
    other: 'Diğer',
  },
  kind: {
    sign_on: 'Katılış',
    sign_off: 'Ayrılış',
    rank_change: 'Rütbe değişikliği',
    amend: 'Düzeltildi',
    cancel_joining: 'Katılış iptal edildi',
    revoke_sign_off: 'Ayrılış geri alındı',
  },
  contractStatus: {
    planned: 'Planlı',
    active: 'Sürüyor',
    overdue: 'Süresi geçti',
    completed: 'Tamamlandı',
  },
  dialog: {
    signOnTitle: 'Katılış Ekle',
    signOnFor: '{{name}} için katılış',
    signOffTitle: '{{name}} için ayrılış',
    rankChangeTitle: '{{name}} için rütbe değişikliği',
    amendTitle: '{{name}} kaydını düzelt',
    cancelTitle: '{{name}} katılışını iptal et',
    revokeTitle: '{{name}} ayrılışını geri al',
    historyTitle: '{{name}} kayıt geçmişi',
    person: 'Personel',
    personPlaceholder: 'Personel kartı ara',
    personHelp: 'Yalnız personel kartı olanlar listelenir. Uyruk ve doğum tarihi zorunludur.',
    vessel: 'Gemi',
    rank: 'Rütbe',
    joiningDate: 'Katılış tarihi',
    signOffDate: 'Ayrılış tarihi',
    newRankFrom: 'Yeni rütbenin ilk günü',
    newRankHelp: 'Mevcut kayıt bir gün önce kapanır; kesintisiz hizmet korunur.',
    joinedAs: '{{date}} tarihinde {{rank}} olarak katıldı',
    joinsAs:
      '{{date}} tarihinde {{rank}} olarak katılacak (henüz katılmadı). Katılıştan önce rütbeyi düzeltmek için Düzelt’i (Amend) kullanın.',
    port: 'Liman',
    portCode: 'UN/LOCODE',
    portCodePlaceholder: 'ör. TRMER',
    contractEnd: 'Kontrat bitişi',
    contractNo: 'Kontrat No',
    monthlyWage: 'Aylık ücret',
    wageCurrency: 'Ücret para birimi',
    wageHelp:
      'İsteğe bağlı. Ofis Payroll dönemini açarken kullanılır; yalnız Payroll izni olan görür.',
    contractReason: 'Gerekçe (12 ayı aşan kontrat)',
    contractReasonHelp:
      'MLC’ye göre gemide son gün {{date}}. Daha uzun kontrat için gerekçe gerekir.',
    reason: 'Neden',
    remarks: 'Not',
    remarksRequired: '"Diğer" seçildiğinde not zorunludur.',
    correctionReason: 'Değişikliğin gerekçesi',
    correctionHelp: 'Katılış ya da ayrılış gerçekleşmişse zorunludur.',
    cancelHelp: 'Yalnız gerçekleşmemiş katılış iptal edilir. Kayıt geçmişte kalır.',
    revokeHelp: 'Kayıt yeniden açık olur.',
    cancel: 'Vazgeç',
    save: 'Kaydet',
    signOn: 'Katılışı Kaydet',
    signOff: 'Ayrılışı Kaydet',
    confirmCancel: 'Katılışı iptal et',
    confirmRevoke: 'Ayrılışı geri al',
    warnings: 'Katılış kaydedildi. Katılmadan önce kontrol edin:',
    close: 'Kapat',
    historyEmpty: 'Geçmiş yok.',
    by: '{{name}} tarafından',
  },
  warnings: {
    PASSPORT_MISSING: 'Personel kartında geçerli pasaport yok.',
    SEAMAN_BOOK_MISSING: 'Personel kartında geçerli gemici cüzdanı yok.',
  },
  errors: {
    VALIDATION_FAILED: 'İşaretli alanları kontrol edin.',
    INVALID_LOCAL_DATE: 'Geçerli bir tarih girin.',
    INVALID_DATE_RANGE: 'Tarihler sırasız.',
    CREW_PROFILE_INCOMPLETE: 'Katılıştan önce personel kartında uyruk ve doğum tarihi olmalı.',
    UNDER_MINIMUM_AGE: 'Personel katılış gününde 16 yaşından küçük (MLC A1.1).',
    CREW_ASSIGNMENT_OVERLAP: 'Personel bu tarihlerde başka bir kayıtta.',
    DATE_TOO_FAR: 'Tarih en çok bir yıl ileri olabilir.',
    SIGN_OFF_BEFORE_JOINING: 'Ayrılış tarihi katılıştan önce olamaz.',
    RANK_CHANGE_BEFORE_JOINING:
      'Yeni rütbe katılış tarihinden ({{date}}) sonra başlamalı. Katılıştan önce rütbeyi düzeltmek için Düzelt’i (Amend) kullanın.',
    RANK_CHANGE_AFTER_SIGN_OFF:
      'Yeni rütbe ayrılış tarihinde ({{date}}) ya da önce başlamalı. Ayrılışı değiştirmek için Düzelt’i (Amend) kullanın.',
    REASON_REQUIRED: 'Gerekçe girin.',
    ALREADY_SIGNED_OFF: 'Bu kaydın ayrılışı var. Değiştirmek için Düzelt’i kullanın.',
    NOT_SIGNED_OFF: 'Bu kaydın ayrılışı yok.',
    JOINING_COMPLETED: 'Katılış gerçekleşti, iptal edilemez. Ayrılış kaydedin ya da düzeltin.',
    CONTRACT_EXCEEDS_MLC: 'Kontrat MLC 12 ay sınırını aşıyor. Gerekçe girin.',
    USE_SIGN_ON_OFF: 'Bu kişinin personel kartı var. Kaydı Katılış / Ayrılış’ta değiştirin.',
    NO_CHANGE: 'Değişiklik yok.',
    UNKNOWN_VESSEL: 'Bir gemi seçin.',
    UNKNOWN_ROLE: 'Bir rütbe seçin.',
    ROLE_KIND_MISMATCH: 'Ofis ünvanı rütbe olarak verilemez. Bir gemi rütbesi seçin.',
    ROW_VERSION_CONFLICT: 'Kayıt başkası tarafından değiştirildi. Yenileyip yeniden deneyin.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    NOT_FOUND: 'Kayıt bulunamadı.',
    GENERIC: 'Kayıt kaydedilemedi. Yeniden deneyin.',
  },
};
